import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { OwnershipTimelineEvent } from '../../services/apiClient'
import { Loader2, X } from 'lucide-react'
import { CountryFlagIcon } from '../ui'
import { formatDate } from '../../utils/format'

const DAY_MS = 86_400_000

function toYmd(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function ymdToDays(ymd: string, originMs: number): number {
  return Math.round((Date.parse(`${ymd}T00:00:00Z`) - originMs) / DAY_MS)
}

const KIND_LABEL: Record<OwnershipTimelineEvent['kind'], string> = {
  in: 'Ny ejer',
  out: 'Udtrådt',
  change: 'Ændret andel',
}

interface OwnershipOverTimeProps {
  events: OwnershipTimelineEvent[]
  loading: boolean
  /** Founding date (lower end of the slider when older than the first event). */
  startdato?: string | null
  /** Selected date (YYYY-MM-DD) or null for today. */
  asOf: string | null
  onChange: (asOf: string | null) => void
  /** Close the panel (and go back to today). */
  onClose?: () => void
}

/**
 * »Historisk ejerskab« (DESIGN.md → Ejerskabsgrafen), docked under the graph
 * when the clock in its toolbar is on: a date field, »I dag«, a slider with a
 * dot per ownership event (red when the owner is foreign) and the events.
 * Picking a date redraws the graph as of then.
 */
const OwnershipOverTime: React.FC<OwnershipOverTimeProps> = ({
  events,
  loading,
  startdato,
  asOf,
  onChange,
  onClose,
}) => {
  const today = toYmd(new Date())
  // The slider spans the ownership history (from a year before the first
  // event, never before the founding); the date field reaches back to the founding.
  const founded = startdato ? startdato.slice(0, 10) : null
  const first = useMemo(() => {
    const dates = events.map((e) => e.date).sort()
    if (dates.length > 0) {
      const before = toYmd(new Date(Date.parse(`${dates[0]}T00:00:00Z`) - 365 * DAY_MS))
      return founded && founded > before ? founded : before
    }
    return founded ?? toYmd(new Date(Date.now() - 365 * DAY_MS))
  }, [events, founded])
  const earliest = founded && founded < first ? founded : first
  const originMs = Date.parse(`${first}T00:00:00Z`)
  const span = Math.max(1, ymdToDays(today, originMs))

  const [draft, setDraft] = useState(asOf ?? today)
  const [dragging, setDragging] = useState(false)
  const draggingRef = useRef(false)
  useEffect(() => setDraft(asOf ?? today), [asOf, today])

  // Commit date-field and keyboard moves after a short pause; a drag commits on release.
  useEffect(() => {
    if (dragging || draft === (asOf ?? today)) return
    const id = window.setTimeout(() => onChange(draft === today ? null : draft), 450)
    return () => window.clearTimeout(id)
  }, [draft, asOf, today, onChange, dragging])

  const value = Math.min(span, Math.max(0, ymdToDays(draft, originMs)))

  // Let go close to a dot and the slider lands on that change: the exact day of it.
  const snapDays = Math.max(3, Math.round(span * 0.02))
  const nearEvent = useMemo(() => {
    let best: string | null = null
    let bestGap = Infinity
    for (const e of events) {
      const gap = Math.abs(ymdToDays(e.date, originMs) - value)
      if (gap <= snapDays && gap < bestGap) {
        best = e.date
        bestGap = gap
      }
    }
    return best
  }, [events, originMs, value, snapDays])
  const release = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    const at = nearEvent && nearEvent < today ? nearEvent : draft
    setDraft(at)
    onChange(at >= today ? null : at)
  }

  // ↑/↓ in the list: the newer or older change, as if clicked.
  const stepInList = (e: React.KeyboardEvent<HTMLOListElement>) => {
    const keys: Record<string, number> = { ArrowUp: -1, ArrowDown: 1, Home: -Infinity, End: Infinity }
    if (!(e.key in keys)) return
    const buttons = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('button')]
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    const step = keys[e.key]
    const next = Number.isFinite(step)
      ? Math.min(buttons.length - 1, Math.max(0, (at < 0 ? -1 : at) + step))
      : step < 0 ? 0 : buttons.length - 1
    e.preventDefault()
    buttons[next]?.focus()
    buttons[next]?.click()
  }

  // Keep the chosen change in sight in the short list.
  const listRef = useRef<HTMLOListElement>(null)
  useEffect(() => {
    listRef.current?.querySelector('[aria-current]')?.scrollIntoView?.({ block: 'nearest' })
  }, [asOf])

  return (
    <div
      className="px-4 pb-3.5 pt-3"
      data-testid="ownership-over-time"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && onClose) {
          e.stopPropagation()
          onClose()
        }
      }}
    >
      <div className="flex items-center gap-3">
        <h3 className="text-[13px] font-semibold text-ink">Historisk ejerskab</h3>
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-[13px] text-ink-500" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Henter…
          </span>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="-mr-1.5 ml-auto rounded-md p-1.5 text-ink-500 hover:bg-rule-soft hover:text-ink"
            aria-label="Luk historisk ejerskab"
            title="Luk og vis i dag"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      <div className={`mt-2 grid gap-x-8 gap-y-3 ${events.length > 0 ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,320px)]' : ''}`}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[13px] text-ink-500" htmlFor="ownership-as-of">
              Vis pr.
            </label>
            <input
              id="ownership-as-of"
              type="date"
              min={earliest}
              max={today}
              value={draft}
              onChange={(e) => e.target.value && setDraft(e.target.value)}
              className={`h-8 rounded-md border bg-paper px-2 text-sm font-semibold tabular-nums ${
                asOf ? 'border-history-300 text-history-700' : 'border-rule-strong text-ink'
              }`}
            />
            <button
              type="button"
              onClick={() => onChange(null)}
              disabled={asOf === null}
              className="h-8 rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300 disabled:opacity-40"
            >
              I dag
            </button>
          </div>

          <div className="relative mt-4">
            <div className="pointer-events-none absolute inset-x-0 top-[3px] h-2" aria-hidden>
              {events.map((e, i) => (
                <span
                  key={`${e.date}-${i}`}
                  data-near={dragging && nearEvent === e.date ? 'true' : undefined}
                  className={`absolute top-0 h-2 w-2 -translate-x-1/2 rounded-full transition-transform duration-150 ease-out ${
                    e.countryCode ? 'bg-signal' : 'bg-ink-400'
                  } ${dragging && nearEvent === e.date ? 'scale-[1.6] ring-2 ring-history-300' : ''}`}
                  style={{ left: `${(Math.min(span, Math.max(0, ymdToDays(e.date, originMs))) / span) * 100}%` }}
                />
              ))}
            </div>
            <input
              type="range"
              min={0}
              max={span}
              value={value}
              aria-label="Dato for ejerskab"
              aria-valuetext={formatDate(draft) ?? draft}
              onChange={(e) => setDraft(toYmd(new Date(originMs + Number(e.target.value) * DAY_MS)))}
              onPointerDown={() => {
                draggingRef.current = true
                setDragging(true)
              }}
              onPointerUp={release}
              onPointerCancel={release}
              onLostPointerCapture={release}
              className={`relative w-full ${asOf ? 'accent-[#8A5A00]' : 'accent-[#12151A]'}`}
            />
            <div className="mt-0.5 flex justify-between text-xs text-ink-500 tabular-nums">
              <span>{formatDate(first)}</span>
              <span>I dag</span>
            </div>
          </div>
        </div>

        {events.length > 0 && (
          <ol
            ref={listRef}
            className="max-h-[132px] overflow-y-auto overflow-x-hidden [scrollbar-width:thin] [scrollbar-color:#C3C9D1_transparent] divide-y divide-rule-soft border-t border-rule-soft md:border-l md:border-t-0 md:pl-4"
            aria-label="Ejerskabsændringer"
            aria-description="Pil op og ned skifter til den nyere eller ældre ændring."
            onKeyDown={stepInList}
          >
            {[...events].reverse().map((e, i) => (
              <li key={`${e.date}-${e.name}-${i}`}>
                <button
                  type="button"
                  onClick={() => onChange(e.date >= today ? null : e.date)}
                  aria-current={asOf === e.date ? 'date' : undefined}
                  className={`flex w-full items-center gap-3 rounded px-1.5 py-1.5 text-left text-sm ${
                    asOf === e.date ? 'bg-history-50' : 'hover:bg-canvas'
                  }`}
                >
                  <span className="w-[5.5rem] shrink-0 text-xs text-ink-500 tabular-nums">{formatDate(e.date)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-1.5 font-semibold text-ink">
                      <span className="truncate">{e.name}</span>
                      {e.countryCode && e.countryCode !== 'XX' && (
                        <CountryFlagIcon code={e.countryCode} focusable={false} />
                      )}
                    </span>
                    <span className="block text-xs text-ink-500">
                      {e.beneficial ? (e.kind === 'out' ? 'Reel ejer ud' : 'Reel ejer') : KIND_LABEL[e.kind]}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

export default OwnershipOverTime
